/**
 * Document Template Service - CRUD Operations and Sync
 *
 * Data reading should be done via useLiveQuery from Dexie React hooks.
 * This service provides CRUD operations, utility functions, and server sync.
 */

import db from '@/db';
import { DocumentTemplate } from '../types/document_template';
import { API_ENDPOINTS } from '../../config/env';
import { getCurrentPerson } from './user_service';
import { fetchWithCache } from '../../lib/api-client';
import { deleteCacheEntry } from './http_cache_service';
import { fetchCollection } from '../../lib/paged-collection';
import { refreshHelpDocuments } from './help_document_service';

import { createLogger, LogLevel } from '../../lib/logger';
import { isOnline } from '../../lib/connectivity';

const logger = createLogger('DocumentTemplate', LogLevel.DEBUG);

// API may return { records: [...] }, { document_templates: [...] }, { templates: [...] }, or just [...]
function parseTemplatesPage(data: unknown): DocumentTemplate[] | null {
  const body = data as DocumentTemplate[] | { records?: DocumentTemplate[]; document_templates?: DocumentTemplate[]; templates?: DocumentTemplate[] } | null;
  const templates = Array.isArray(body) ? body : body?.records || body?.document_templates || body?.templates;

  if (!templates || !Array.isArray(templates)) {
    logger.warn('[TemplateService] Unexpected API response format:', data);
    return null;
  }

  return templates;
}


/**
 * Refresh document templates from the server.
 * Every page is fetched with its own conditional GET (lib/paged-collection):
 * templates from changed pages are stored, and templates the server no longer
 * lists are removed once every page has answered.
 */
export async function refreshTemplates(): Promise<void> {
  const currentPerson = await getCurrentPerson();

  if (!currentPerson) {
    logger.debug('[TemplateService] No current person - skipping refresh');
    return;
  }

  const collection = await fetchCollection<DocumentTemplate>({
    organizationId: currentPerson.organization.id,
    // Templates belong to the organization, so the organization scope on the
    // cache row is the whole key; client assignments belong to the person,
    // which is why refreshClients keys on the person as well.
    cacheKey: 'templates',
    url: page => `${API_ENDPOINTS.documentTemplates}?current_person_id=${currentPerson.id}&page=${page}`,
    parse: parseTemplatesPage,
    localIds: async ids => new Set(await db.documentTemplates.where('id').anyOf(ids as number[]).filter(t => t.organization_id === currentPerson.organization.id).primaryKeys()),
    timeout: 30000,
  });

  if (!collection.firstPageOk) {
    if (collection.status === 404) {
      logger.debug('[TemplateService] Templates endpoint not available');
      return;
    }
    if (collection.httpOk) return; // Unusable body — already logged by the parser.
    if (collection.status === 304) return; // A 304 with no manifest to read it against; keep local rows, ask again next time.
    throw new Error(`Failed to fetch templates: ${collection.status}`);
  }

  const organizationId = currentPerson.organization.id;

  // Store the rows that changed and, when every page answered, remove the
  // templates the server no longer lists — one transaction. Reconciling runs on
  // every complete walk, unchanged or not.
  const staleIds = await db.transaction('rw', db.documentTemplates, async () => {
    if (collection.records.length > 0) {
      await db.documentTemplates.bulkPut(collection.records.map(template => ({ ...template, organization_id: organizationId })));
    }
    if (!collection.complete) return [];

    const localTemplates = await db.documentTemplates
      .where('organization_id')
      .equals(organizationId)
      .toArray();
    const stale = localTemplates.filter(t => !collection.ids.has(t.id)).map(t => t.id);
    if (stale.length > 0) {
      await db.documentTemplates.bulkDelete(stale);
      logger.debug(`[TemplateService] Removed ${stale.length} stale template(s)`);
    }
    return stale;
  });

  if (!collection.complete) {
    logger.warn('[TemplateService] The templates walk was incomplete; keeping local templates until the next refresh');
  }
  // A pruned template's own validator would 304 into a row that is gone.
  for (const id of staleIds) await deleteCacheEntry({ organizationId, name: `template_${id}` });
  await collection.commit();

  logger.debug(collection.unchanged
    ? `[TemplateService] Templates unchanged across ${collection.totalPages} page(s)`
    : `[TemplateService] Refreshed ${collection.records.length} changed template(s) across ${collection.totalPages} page(s)`);

  // Fetch help documents for templates that have them
  await refreshHelpDocuments();
}

/**
 * Pull a single template from the server and update IndexedDB
 *
 * @param templateId The template ID to fetch
 * @returns The updated template, or null if not found
 */
export async function pullTemplate(templateId: number): Promise<DocumentTemplate | null> {
  const currentPerson = await getCurrentPerson();

  if (!currentPerson) {
    logger.debug('[TemplateService] No current person - skipping pull');
    return null;
  }

  if (!isOnline()) {
    logger.debug('[TemplateService] Offline - skipping pull');
    return null;
  }

  try {
    const url = `${API_ENDPOINTS.documentTemplates}/${templateId}?current_person_id=${currentPerson.id}`;
    const { response, notModified, commit } = await fetchWithCache(url, {
      organizationId: currentPerson.organization.id,
      name: `template_${templateId}`,
    }, {
      timeout: 10000,
      haveBody: async () => (await db.documentTemplates.get(templateId))?.organization_id === currentPerson.organization.id,
    });

    if (notModified) {
      return await db.documentTemplates.get(templateId) ?? null;
    }

    if (!response.ok) {
      logger.warn(`[TemplateService] Failed to pull template ${templateId}: ${response.status}`);
      return null;
    }

    const serverTemplate: DocumentTemplate = await response.json();

    const templateWithMeta = {
      ...serverTemplate,
      organization_id: currentPerson.organization.id,
    };

    await db.documentTemplates.put(templateWithMeta);
    await commit();
    return templateWithMeta;
  } catch (error) {
    logger.error(`[TemplateService] Error pulling template ${templateId}:`, error);
    return null;
  }
}
